<template>
  <!--
    Issue #46 — NotificationCard.vue: tarjeta de notificación individual (tareas 16.3–16.6)
  -->
  <div
    class="ncard"
    :class="[`ncard--${notification.type}`, `ncard--priority-${notification.priority}`, { 'ncard--unread': !notification.read }]"
    role="listitem"
    :aria-label="`Notificación: ${notification.title}${!notification.read ? ', sin leer' : ''}`"
  >
    <!-- Franja de prioridad -->
    <div class="ncard-priority-stripe" :class="`stripe--${notification.priority}`" aria-hidden="true" />

    <!-- Ícono del tipo (tarea 16.3.1) -->
    <div class="ncard-icon-wrap" :class="`icon-wrap--${notification.type}`" aria-hidden="true">
      <i :class="typeIcon" />
    </div>

    <!-- Contenido -->
    <div class="ncard-content">
      <div class="ncard-top">
        <!-- Título + badge de prioridad -->
        <span class="ncard-title">{{ notification.title }}</span>
        <span
          v-if="notification.priority === 'critica' || notification.priority === 'alta'"
          class="ncard-priority-badge"
          :class="`priority-badge--${notification.priority}`"
        >
          {{ notification.priority === 'critica' ? 'Crítica' : 'Alta' }}
        </span>
      </div>

      <!-- Mensaje -->
      <p class="ncard-message">{{ notification.message }}</p>

      <!-- Timestamp (tarea 16.2.4) -->
      <time class="ncard-time" :datetime="notification.timestamp">
        {{ relativeTime }}
      </time>

      <!-- Acciones rápidas (tarea 16.3.4) -->
      <div class="ncard-actions" v-if="notification.action || !notification.read">
        <button
          v-if="notification.action"
          class="ncard-action-btn"
          type="button"
          :aria-label="`${notification.action.label} para notificación: ${notification.title}`"
          @click="handleAction"
        >
          {{ notification.action.label }}
          <i class="pi pi-arrow-right" aria-hidden="true" />
        </button>
        <button
          v-if="!notification.read"
          class="ncard-mark-read"
          type="button"
          :aria-label="`Marcar como leída: ${notification.title}`"
          @click="$emit('mark-read')"
        >
          <i class="pi pi-check" aria-hidden="true" /> Marcar leída
        </button>
      </div>
    </div>

    <!-- Botón descartar (tarea 16.3.4) -->
    <button
      class="ncard-dismiss"
      type="button"
      :aria-label="`Descartar notificación: ${notification.title}`"
      @click="$emit('dismiss')"
    >
      <i class="pi pi-times" aria-hidden="true" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import type { Notification } from '@/stores/notifications'

const props = defineProps<{ notification: Notification }>()

const emit = defineEmits<{
  (e: 'mark-read'): void
  (e: 'dismiss'): void
}>()

const router = useRouter()

// ── Ícono según tipo (tarea 16.3.1) ───────────────────────────────────────────
const TYPE_ICONS: Record<string, string> = {
  info:        'pi pi-info-circle',
  advertencia: 'pi pi-exclamation-triangle',
  error:       'pi pi-times-circle',
  exito:       'pi pi-check-circle',
}

const typeIcon = computed(() => TYPE_ICONS[props.notification.type] ?? 'pi pi-bell')

// ── Tiempo relativo (tarea 16.2.4) ────────────────────────────────────────────
const relativeTime = computed(() => {
  const ts = new Date(props.notification.timestamp)
  const diff = Date.now() - ts.getTime()
  const min = Math.floor(diff / 60_000)
  if (min < 1) return 'Ahora mismo'
  if (min < 60) return `Hace ${min} min`
  const hrs = Math.floor(min / 60)
  if (hrs < 24) return `Hace ${hrs} h`
  const days = Math.floor(hrs / 24)
  return `Hace ${days} día${days > 1 ? 's' : ''}`
})

// ── Acción ────────────────────────────────────────────────────────────────────
function handleAction() {
  const action = props.notification.action
  if (!action) return
  if (action.fn) {
    action.fn()
  } else if (action.route) {
    router.push(action.route)
  }
  emit('mark-read')
}
</script>

<style scoped>
/* ── Tarjeta base ── */
.ncard {
  display: flex; align-items: flex-start; gap: 0; position: relative;
  padding: 0.875rem 0.875rem 0.875rem 0;
  border-bottom: 1px solid var(--surface-d, #e5e7eb);
  transition: background 0.15s;
  background: var(--surface-card, #fff);
}
.ncard--unread { background: color-mix(in srgb, var(--primary-color, #6366f1) 4%, var(--surface-card, #fff)); }
.ncard:hover { background: var(--surface-ground, #f8fafc); }

/* ── Franja de prioridad ── */
.ncard-priority-stripe { width: 4px; align-self: stretch; flex-shrink: 0; border-radius: 0 0 0 0; }
.stripe--baja    { background: #94a3b8; }
.stripe--media   { background: #3b82f6; }
.stripe--alta    { background: #f59e0b; }
.stripe--critica { background: #ef4444; }

/* ── Ícono ── */
.ncard-icon-wrap {
  width: 36px; height: 36px; flex-shrink: 0;
  border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: 1rem; margin: 0 0.625rem;
}
.icon-wrap--info        { background: #dbeafe; color: #2563eb; }
.icon-wrap--advertencia { background: #fef3c7; color: #b45309; }
.icon-wrap--error       { background: #fee2e2; color: #dc2626; }
.icon-wrap--exito       { background: #dcfce7; color: #16a34a; }

/* ── Contenido ── */
.ncard-content { flex: 1; display: flex; flex-direction: column; gap: 0.25rem; min-width: 0; }
.ncard-top { display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap; }
.ncard-title { font-size: 0.83rem; font-weight: 600; color: var(--text-color, #1e293b); }
.ncard-priority-badge { font-size: 0.62rem; font-weight: 700; padding: 0.1rem 0.4rem; border-radius: 99px; }
.priority-badge--alta { background: #fef3c7; color: #92400e; }
.priority-badge--critica { background: #fee2e2; color: #991b1b; }
.ncard-message { font-size: 0.78rem; color: var(--text-color-secondary, #64748b); margin: 0; line-height: 1.4; }
.ncard-time { font-size: 0.68rem; color: #94a3b8; }
.ncard-actions { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; margin-top: 0.25rem; }
.ncard-action-btn { background: none; border: none; cursor: pointer; font-size: 0.75rem; color: var(--primary-color, #6366f1); font-weight: 600; display: flex; align-items: center; gap: 0.25rem; padding: 0; }
.ncard-mark-read { background: none; border: none; cursor: pointer; font-size: 0.72rem; color: var(--text-color-secondary, #64748b); display: flex; align-items: center; gap: 0.2rem; padding: 0; }

/* ── Botón descartar ── */
.ncard-dismiss {
  background: none; border: none; cursor: pointer;
  color: var(--text-color-secondary, #94a3b8);
  font-size: 0.75rem; padding: 0.2rem; flex-shrink: 0;
  border-radius: 0.25rem; align-self: flex-start;
}
.ncard-dismiss:hover { color: #ef4444; }
.ncard-dismiss:focus { outline: 2px solid var(--primary-color, #6366f1); }
</style>
